<script setup lang="ts">

import {ref} from "vue";

import {useReCaptcha} from "vue-recaptcha-v3";
import {useI18n} from "vue-i18n";
import * as CoreService from "@/services/core.service";
import CustomButton from "@/components/Button/Button.vue";
import {usePageSEO} from "@/composables/usePageSEO";
import {trackEvent} from "@/services/event.service";
import {parsePhoneNumber} from "awesome-phonenumber";
import {VPhoneInput} from "v-phone-input";
import {useLocalization} from "@/locales/localization";


const {executeRecaptcha, recaptchaLoaded} = useReCaptcha()
const { currentLocale } = useLocalization();


const {t} = useI18n(); // Import the translation function
usePageSEO('help')

//TODO translate
const loading = ref(false);
const form = ref({})

const formData = ref({
  name: "",
  telephone: "",
  email: "",
  message: "",
  agreed: false,
});

const inputs = ref([
  {
    label: t('supportHelp.items[0].label'), //name
    placeholder: t('supportHelp.items[0].placeholder')
  },
  {
    label: t('supportHelp.items[1].label'), //Telephone
    placeholder: t('supportHelp.items[1].placeholder')
  },
  {
    label: t('supportHelp.items[2].label'), //email
    placeholder: t('supportHelp.items[2].placeholder')
  },
  {
    label: t('supportHelp.items[3].label'),
    placeholder: t('supportHelp.items[3].placeholder')
  }
]);

const valid = ref(true)
const state = ref<'idle' | 'submit' | 'error'>('idle');

const requestHelp = () => {
  form.value.validate().then(async (result) => {
    if (!result.valid) {
      return;
    }

    formData.value = {
      ...formData.value,   
      telephone: parsePhoneNumber(formData.value.telephone).number?.international
    }
    
    trackEvent('cosmosy.ga.helpPage.contactForm', {
      event_label: 'contact_form',
      userData: {
        fullname: formData.value.name,
        email: formData.value.email,
        phone: formData.value.telephone
      }

    });
    
    loading.value = true;

    try {
      await recaptchaLoaded();
      await executeRecaptcha("help_request");

      await CoreService.sendHelpRequest(formData.value, currentLocale.value);

      state.value = "submit";

      // Clear the form
      Object.keys(formData.value).forEach((key) => {
        if (typeof formData.value[key] === "boolean") {
          formData.value[key] = false; // Reset checkboxes
        } else {
          formData.value[key] = ""; // Reset text fields
        }
      });

      // Reset form validation
      form.value.reset();

      // Hide success message after 5 seconds
      setTimeout(() => {
        state.value = "idle";
      }, 5000);
    } catch (error) {
      state.value = "error"; // Handle error state
    } finally {
      loading.value = false;
    }
  });
};
</script>


<template>
  <div class="bg-background-light border-b-[1px] border-bw-grey min-h-screen">
    <!-- Main Content -->
    <div class="container mx-auto flex flex-col md:flex-row items-center pt-12 pb-5 md:pt-20 md:pb-10">
      <!-- Text Section -->
      <div class="md:w-1/2 px-4 md:text-left">
        <h1 class="text-3xl md:text-4xl font-bold mb-4">{{ t('supportHelp.title') }}</h1>
        <h2 class="text-xl md:text-2xl font-semibold mb-4 text-primary-blue">{{ t('supportHelp.subtitle') }}</h2>
        <p class="text-gray-600 mb-6">
          {{ t('supportHelp.body') }}
        </p>
      </div>

      <!-- Illustration Section -->
      <div class="md:w-1/2 flex justify-center">
        <img
            src="@/assets/images/bottle.svg"
            alt="Contact Us Illustration"
            class="max-w-full"
        />
      </div>
    </div>


    <!-- Form -->
    <div class="container mx-auto py-8 md:py-12 px-4">
      <div class="bg-gradient-to-r rounded-8xl from-gradients-ghostWhite to-gradients-aliceBlue p-8 md:p-12">
        <div class="w-full md:w-2/3 lg:w-1/2 mx-auto flex flex-col gap-4 py-12">
          <!-- Form Title -->
          <h2 class="text-2xl font-bold text-gray-700 ">{{ t('supportHelp.contactInfo') }}</h2>
          <p class="text-sm text-gray-500 ">
            {{ t('supportHelp.formText') }}
          </p>

          <v-form ref="form" v-model="valid"
                  class="mt-10 flex flex-col gap-6">

            <div>
              <VTextField
                  v-model="formData.name"
                  :label= inputs[0].label
                  type="text"
                  variant="underlined"
                  :placeholder= inputs[0].placeholder
                  required
                  :disabled="loading"
                  :rules="[
                      v => !!v || t('supportHelp.fillRequired'),
                      v => /^[\p{L}\p{M} ]+$/u.test(v) || t('supportHelp.validValue')
                  ]"

              />
            </div>

            <!-- Telephone and Email -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <v-phone-input
                  v-model="formData.telephone"
                  :default-country="'GR'"
                  :invalidMessage = "t('enterValidPhone')"
                  :label = inputs[1].label
                  :only-country-code="false"
                  :return-object="true"
                  required
                  :disabled="loading"
                  density="comfortable"
                  :use-default-validation="false"
                  :rules="[
                    v => !!v || t('supportHelp.fillRequired'),
                    v => /^[\d+ ]+$/.test(v) || t('supportHelp.validValue')
                  ]"

              />
              <VTextField
                  v-model="formData.email"
                  :label= inputs[2].label
                  type="email"
                  variant="underlined"
                  :placeholder= inputs[2].placeholder
                  required
                  :disabled="loading"
                  :rules="[
                      v => !!v || t('supportHelp.fillRequired'),
                      v => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || t('supportHelp.validValue')
                  ]"

              />
            </div>

            <!-- Message -->
            <div>
              <VTextarea
                  v-model="formData.message"
                  :label= inputs[3].label
                  type="textarea"
                  variant="underlined"
                  :placeholder= inputs[3].placeholder
                  rows="4"
                  :disabled="loading"
                  :rules="[v => !!v || t('supportHelp.fillRequired')]"

              />
            </div>
            <!-- Message -->
            <div>
              <v-checkbox
                  v-model="formData.agreed"
                  :disabled="loading"
                  required
                  :rules="[v => !!v || t('supportHelp.pleaseAgree')]"
              >
                <template v-slot:label>
                  {{ t('supportHelp.agree') }}&nbsp;<a
                    class="link underline"
                    href="/privacy"
                    target="_blank"
                    @click.stop
                >
                  {{ t('supportHelp.privacyPolicy') }}
                </a> &nbsp;{{ t('supportHelp.zu') }}.
                </template>
              </v-checkbox>
            </div>

          </v-form>

          <div class="flex flex-auto my-3 gap-3">

            <CustomButton type="black"
                          :rounded="true"
                          :outlined="false"
                          :disabled="loading"
                          class="flex-1"
                          @click="requestHelp"
            >
              {{ t('supportHelp.submit') }}
            </CustomButton>

          </div>


          <div v-if="state === 'submit'" class="text-green-600 mt-4">
            {{ t('supportHelp.thanksForMessage') }}
          </div>
          <div v-if="state === 'error'" class="text-red-600 mt-4">
            {{ t('supportHelp.unexpectedError') }}
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background-color: #fff;
  outline: none;
  transition: border-color 0.2s;
}

.form-input:focus {
  border-color: #1b84ff;
  box-shadow: 0 0 0 1px #1b84ff;
}

textarea.form-input {
  min-height: 100px;
}

textarea.form-input::placeholder,
input.form-input::placeholder {
  color: #9ca3af;
}
</style>
